import { SettlementChances } from "../../shared/settlementProspects";
import React from "react";
import { PartyAgreements } from "../../shared/Agreements.ts";
import { CaseCounts } from "../../shared/CaseCounts.ts";
import {
  BookedReserve,
  ClaimKind,
  Case,
  CaseParameters,
  Claim,
  Damage,
  Configuration,
  CostStructure,
  Party,
  Results,
  SettlementOffer,
} from "../../shared/CaseModel";
import { Timeline } from "../../shared/timeline";
import { CaseListElement } from "../../shared/CasesList.ts";
import { Budget } from "../../shared/CostModel";
import { CaseFilter, QueryOptions } from "../../shared/parse/CasesQuery.ts";
import {
  PortfolioOverview,
  PortfolioStats,
  SettlementPortfolio,
} from "../../shared/PortfolioTypes.ts";
import { DecisionTreeResult } from "./decisionTrees.ts";

export interface CaseApi {
  // returns list of basic case data e.g. for showing a case list; omit
  // `options` to get ALL of the user's cases (no pagination), e.g. for the
  // redesigned list which sorts/filters/paginates client-side
  getCasesList(options?: QueryOptions): Promise<CaseListElement[]>;
  // The names the access list of a case can know the signed-in user under
  // (see canWrite): with them the app tells the cases the user may only read.
  queryAccessNames(): Promise<string[]>;
  getCaseCounts(filter?: CaseFilter): Promise<CaseCounts>;
  getPortfolioStats(filter?: CaseFilter): Promise<PortfolioStats>;
  // raw per-item settlement arrays for the settlement sub-area's charts,
  // loaded lazily by the /portfolio/settlements route (kept off getPortfolioStats)
  getSettlementPortfolio(filter?: CaseFilter): Promise<SettlementPortfolio>;
  // the start page's figures of the portfolio over all the user's cases, of a
  // size that does not grow with the portfolio
  getPortfolioOverview(): Promise<PortfolioOverview>;
  queryCase(caseId: string): Promise<Case>;
  queryVariantResults(
    caseId: string,
  ): Promise<{ [variantId: string]: Results }>;
  // Has the server generate the decision trees of the case, which are never
  // stored, and resolves once that has ended; see fetchDecisionTrees. Only for
  // users the `decisionTree` feature toggle is on for.
  generateDecisionTrees(
    caseId: string,
    onProgress: (share: number) => void,
    signal: AbortSignal,
  ): Promise<DecisionTreeResult>;
  addCase(c: Case): Promise<Case>;
  importCase(
    config: CaseParameters,
    options: { overwriteId?: string },
  ): Promise<Case>;
  removeCase(caseId: string): Promise<void>;
  updateTimeline(caseId: string, timeline: Timeline): Promise<void>;
  updateBookedReserves(
    caseId: string,
    bookedReserves: BookedReserve[],
  ): Promise<void>;
  // a stage closed in the timeline, with what was spent on it in the budgets
  closeStage(
    caseId: string,
    timeline: Timeline,
    budgets: Budget[],
    record?: Pick<Case, "judgment" | "closing">,
  ): Promise<void>;
  // the kind of claim too, where an insurer sets it
  setTitle(caseId: string, title: string, claimKind?: ClaimKind): Promise<void>;
  addParty(caseId: string, party: Party): Promise<Party[]>;
  updateParty(caseId: string, prototype: Party): Promise<void>;
  // a party's apportionment and its costs split by it, in one save
  updateApportionment(
    caseId: string,
    party: Party,
    budget: Budget,
  ): Promise<void>;
  removeParty(caseId: string, partyId: string): Promise<void>;
  addClaim(caseId: string, claim: Claim): Promise<Claim[]>;
  // with a damage to save along, where an amount's damage is new or renamed
  updateClaim(caseId: string, claim: Claim, damage?: Damage): Promise<void>;
  removeClaim(caseId: string, claimId: string): Promise<void>;
  addBudget(caseId: string, budget: Budget): Promise<Budget[]>;
  updateBudget(caseId: string, budget: Budget): Promise<void>;
  removeBudget(caseId: string, partyId: string): Promise<void>;
  // settlement offers
  updateSettlementChances(
    caseId: string,
    settlementChances: SettlementChances[],
  ): Promise<void>;
  addOffer(caseId: string, offer: SettlementOffer): Promise<SettlementOffer[]>;
  updateOffer(caseId: string, offer: SettlementOffer): Promise<void>;
  removeOffer(caseId: string, offerId: string): Promise<void>;

  // fee structures
  updateCostStructure(caseId: string, structure: CostStructure): Promise<void>;

  setBargainingVariant(caseId: string, variant?: Configuration): Promise<void>;

  // agreements
  setPartyAgreements(
    caseId: string,
    partyAgreements: PartyAgreements,
  ): Promise<void>;
}

export const CaseApiContext = React.createContext<CaseApi>({
  getCasesList: function (): Promise<CaseListElement[]> {
    throw new Error("Not initialized");
  },

  queryAccessNames() {
    throw new Error("Not initialized");
  },

  getCaseCounts() {
    throw new Error("Not initialized");
  },

  getPortfolioStats: function (): Promise<PortfolioStats> {
    throw new Error("Not initialized");
  },

  getSettlementPortfolio: function (): Promise<SettlementPortfolio> {
    throw new Error("Not initialized");
  },

  getPortfolioOverview: function (): Promise<PortfolioOverview> {
    throw new Error("Not initialized");
  },

  queryCase: function (): Promise<Case> {
    throw new Error("Not initialized");
  },
  queryVariantResults: function (): Promise<{
    [variantId: string]: Results;
  }> {
    throw new Error("Not initialized");
  },
  generateDecisionTrees: function (): Promise<DecisionTreeResult> {
    throw new Error("Not initialized");
  },
  addCase: function (): Promise<Case> {
    throw new Error("Not initialized");
  },
  importCase: function (): Promise<Case> {
    throw new Error("Not initialized");
  },
  removeCase: function (): Promise<void> {
    throw new Error("Not initialized");
  },
  closeStage: function (): Promise<void> {
    throw new Error("Not initialized");
  },
  updateBookedReserves: function (): Promise<void> {
    throw new Error("Not initialized");
  },
  updateTimeline: function (): Promise<void> {
    throw new Error("Not initialized");
  },
  setTitle: function (): Promise<void> {
    throw new Error("Not initialized");
  },
  addParty: function (): Promise<Party[]> {
    throw new Error("Not initialized");
  },
  updateParty: function (): Promise<void> {
    throw new Error("Not initialized");
  },
  updateApportionment: function (): Promise<void> {
    throw new Error("Not initialized");
  },
  removeParty: function (): Promise<void> {
    throw new Error("Not initialized");
  },
  addClaim: function (): Promise<Claim[]> {
    throw new Error("Not initialized");
  },
  updateClaim: function (): Promise<void> {
    throw new Error("Not initialized");
  },
  removeClaim: function (): Promise<void> {
    throw new Error("Not initialized");
  },
  addBudget: function (): Promise<Budget[]> {
    throw new Error("Not initialized");
  },
  updateBudget: function (): Promise<void> {
    throw new Error("Not initialized");
  },
  removeBudget: function (): Promise<void> {
    throw new Error("Not initialized");
  },
  updateSettlementChances: function (): Promise<void> {
    throw new Error("Function not implemented.");
  },
  addOffer: function (): Promise<SettlementOffer[]> {
    throw new Error("Not initialized");
  },
  updateOffer: function (): Promise<void> {
    throw new Error("Not initialized");
  },
  removeOffer: function (): Promise<void> {
    throw new Error("Not initialized");
  },
  setBargainingVariant: function (): Promise<void> {
    throw new Error("Not initialized");
  },
  updateCostStructure: function (): Promise<void> {
    throw new Error("Not initialized");
  },
  setPartyAgreements: function (): Promise<void> {
    throw new Error("Not initialized");
  },
});
